import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Users, GraduationCap, ShieldCheck, ArrowRight, Phone } from "lucide-react";
import { toast } from "sonner";
import { api, V1 } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useSettings } from "@/lib/settings";
import ConsentModal, { CONSENT_VERSION } from "@/components/ConsentModal";

const GRADES = ["5", "6", "7", "8", "9", "10", "11", "12"];

export default function Register() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const logoSrc = settings?.logo_url 
    ? (settings.logo_url.startsWith('http') ? settings.logo_url : `${process.env.REACT_APP_BACKEND_URL || ''}${settings.logo_url}`)
    : "/logo.png";

  const [role, setRole] = useState("parent"); // parent | student
  const [step, setStep] = useState("form"); // form | code
  const [sending, setSending] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [consent, setConsent] = useState(null); // {kvkk,terms,marketing,version,accepted_at}
  const [refCode, setRefCode] = useState(
    () => new URLSearchParams(window.location.search).get("ref") || ""
  );

  // Common
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");

  // Student self-registration extras
  const [grade, setGrade] = useState("");
  const [parentName, setParentName] = useState("");
  const [parentPhone, setParentPhone] = useState("");
  const [parentEmail, setParentEmail] = useState("");

  const canSend = () => {
    if (!consent) return false;
    if (role === "parent") return name && phone;
    return name && phone && grade && parentName && parentPhone;
  };

  const startRegister = async () => {
    if (!consent) {
      setConsentOpen(true);
      return toast.error("Devam etmek için sözleşme onayı gerekli");
    }
    setSending(true);
    try {
      await api.post(`${V1}/auth/send-code`, { phone, purpose: "register" });
      setStep("code");
      toast.success("Doğrulama kodu telefonunuza gönderildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kod gönderilemedi");
    } finally { setSending(false); }
  };

  const verify = async () => {
    setSending(true);
    try {
      const payload = {
        phone, code, name, role,
        referral_code: refCode || undefined,
        consent: consent ? {
          kvkk: consent.kvkk, terms: consent.terms, marketing: consent.marketing,
          version: consent.version, accepted_at: consent.accepted_at,
        } : undefined,
      };
      if (role === "student") {
        payload.grade = grade;
        payload.parent_name = parentName;
        payload.parent_phone = parentPhone;
        payload.parent_email = parentEmail || undefined;
      }
      const { data } = await api.post(`${V1}/auth/verify-code`, payload);
      await login(data.token, data.user);
      toast.success(
        refCode
          ? "Kayıt tamamlandı! Referans kredin hesabına eklendi 🎁"
          : "Kayıt tamamlandı! Hoş geldin."
      );
      navigate(role === "student" ? "/panel" : "/panel/cocuklarim");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kod doğrulanamadı");
    } finally { setSending(false); }
  };

  const toggleConsentCheckbox = () => {
    if (consent) {
      setConsent(null);
    } else {
      setConsentOpen(true);
    }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-16" data-testid="register-page">
      <div className="text-center mb-8">
        <Link to="/" className="inline-block mb-6">
          <img src={logoSrc} alt="Sano Student Logo" className="h-16 mx-auto object-contain" />
        </Link>
        <div className="overline text-accent">Ücretsiz Kayıt</div>
        <h1 className="font-display text-3xl font-bold mt-2">SanoStudent'a katıl.</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Veli veya öğrenci olarak kayıt ol, deneme sınavları ve bursluluk başvurularına hemen başla.
        </p>
      </div>

      <div className="rounded-xl border border-border bg-card p-6">
        <Tabs value={role} onValueChange={(v) => { setRole(v); setStep("form"); }}>
          <TabsList className="w-full grid grid-cols-2 mb-6">
            <TabsTrigger value="parent" data-testid="reg-tab-parent">
              <Users className="h-4 w-4" /> Veli
            </TabsTrigger>
            <TabsTrigger value="student" data-testid="reg-tab-student">
              <GraduationCap className="h-4 w-4" /> Öğrenci
            </TabsTrigger>
          </TabsList>

          {step === "form" ? (
            <div className="space-y-4">
              {/* Common: name + phone */}
              <div>
                <Label>{role === "parent" ? "Ad Soyad (Veli)" : "Ad Soyad (Öğrenci)"}</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={role === "parent" ? "Örn. Ayşe Yılmaz" : "Örn. Elif Yılmaz"}
                  data-testid="register-name"
                />
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  <Phone className="h-3.5 w-3.5" />
                  {role === "parent" ? "Cep Telefonun" : "Kendi Cep Telefonun"}
                </Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="5XX XXX XX XX"
                  data-testid="register-phone"
                />
                <div className="text-xs text-muted-foreground mt-1">
                  SMS doğrulama kodu bu numaraya gönderilecek.
                </div>
              </div>

              {/* Student extras */}
              <TabsContent value="student" className="space-y-4 mt-0 p-0">
                <div>
                  <Label>Sınıf</Label>
                  <select
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                    className="w-full h-10 rounded-md border border-input bg-transparent px-3 text-sm"
                    data-testid="register-grade"
                  >
                    <option value="">Seçiniz…</option>
                    {GRADES.map((g) => (
                      <option key={g} value={g}>{g}. Sınıf</option>
                    ))}
                  </select>
                </div>
                <div className="p-4 rounded-lg border border-dashed border-border bg-muted/30 space-y-3">
                  <div className="text-xs font-medium text-foreground flex items-center gap-1">
                    <Users className="h-3.5 w-3.5" /> Veli İletişim Bilgileri
                  </div>
                  <div>
                    <Label className="text-xs">Veli Adı Soyadı</Label>
                    <Input
                      value={parentName}
                      onChange={(e) => setParentName(e.target.value)}
                      placeholder="Ör. Ayşe Yılmaz"
                      data-testid="register-parent-name"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Veli Telefonu</Label>
                    <Input
                      value={parentPhone}
                      onChange={(e) => setParentPhone(e.target.value)}
                      placeholder="5XX XXX XX XX"
                      data-testid="register-parent-phone"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Veli E-posta (opsiyonel)</Label>
                    <Input
                      type="email"
                      value={parentEmail}
                      onChange={(e) => setParentEmail(e.target.value)}
                      placeholder="veli@ornek.com"
                      data-testid="register-parent-email"
                    />
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    Bursluluk başvurusu, önemli bildirimler ve KVKK gereği veli iletişim
                    bilgileri zorunludur.
                  </div>
                </div>
              </TabsContent>

              {/* Referral */}
              <div>
                <Label>Davet Kodu (opsiyonel)</Label>
                <Input
                  value={refCode}
                  onChange={(e) => setRefCode(e.target.value.toUpperCase())}
                  placeholder="XXXXXXXX"
                  className="font-mono"
                  data-testid="register-refcode"
                />
                {refCode && (
                  <div className="text-xs text-accent mt-1">
                    Kaydolunca 5 haftalık deneme kredisi kazanacaksın 🎁
                  </div>
                )}
              </div>

              {/* Consent */}
              <label
                className="flex items-start gap-2 text-sm cursor-pointer select-none"
                onClick={(e) => { e.preventDefault(); toggleConsentCheckbox(); }}
              >
                <span
                  className={`mt-0.5 h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${
                    consent ? "bg-accent border-accent" : "border-input bg-transparent"
                  }`}
                  data-testid="register-consent-box"
                >
                  {consent && <ShieldCheck className="h-3.5 w-3.5 text-accent-foreground" />}
                </span>
                <span>
                  <b>KVKK Aydınlatma Metni</b> ve <b>Kullanım Koşulları</b>'nı okumak ve
                  onaylamak için burayı tıklayın.
                  {consent && (
                    <span className="block text-[11px] text-success mt-0.5">
                      ✓ Onaylandı · Sürüm {consent.version}
                    </span>
                  )}
                </span>
              </label>

              <Button
                onClick={startRegister}
                disabled={sending || !canSend()}
                className="w-full"
                data-testid="register-submit"
              >
                {sending ? "Gönderiliyor…" : "Kodu Gönder"} <ArrowRight className="h-4 w-4" />
              </Button>

              <div className="text-xs text-muted-foreground text-center">
                Zaten hesabın var mı?{" "}
                <Link to="/giris" className="text-primary hover:underline">Giriş yap</Link>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label>Doğrulama Kodu</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  maxLength={6}
                  placeholder="6 haneli kod"
                  data-testid="register-code"
                />
                <div className="text-xs text-muted-foreground mt-1">
                  {phone} numarasına gönderildi. 5 dakika geçerlidir.
                </div>
              </div>
              <Button
                onClick={verify}
                disabled={sending || code.length < 4}
                className="w-full"
                data-testid="register-verify"
              >
                {sending ? "Doğrulanıyor…" : "Doğrula ve Kaydı Tamamla"}
              </Button>
              <button
                className="text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setStep("form")}
                type="button"
              >
                ← Bilgileri değiştir
              </button>
            </div>
          )}
        </Tabs>
      </div>

      <ConsentModal
        open={consentOpen}
        onOpenChange={setConsentOpen}
        onAccept={(c) => {
          setConsent(c);
          toast.success("Onayınız kaydedildi ✓");
        }}
      />
    </div>
  );
}
